import React, { useState, useEffect } from 'react';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import Navbar from '../../../shared/components/Navbar';
import DevBanner from '../components/DevBanner';
import NotificationBanner from '../components/NotificationBanner';
import HeroSection from '../components/HeroSection';
import ServicesSection from '../components/ServicesSection';
import AdminSection from '../components/AdminSection';
import ContactSection from '../components/ContactSection';
import PartnersSection from '../components/PartnersSection';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';
import MaintenancePublicPage from '../components/MaintenancePublicPage';
import Footer from '../../../shared/components/Footer';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../../core/config/api';
import type { User } from '../../../shared/types';

const Home: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showDeletionNotice, setShowDeletionNotice] = useState(false);
  const [maintenanceStatus, setMaintenanceStatus] = useState<{
    enabled: boolean;
    message: string | null;
    scheduled_at: string | null;
    scheduled_message: string | null;
  } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Vérifier si l'utilisateur vient d'être supprimé
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('deleted') === '1') {
      setShowDeletionNotice(true);
    }

    // Vérifier le statut de maintenance
    const checkMaintenanceStatus = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000); // Timeout de 2 secondes
        
        const response = await fetch(`${getApiBaseUrl(true)}/admin/maintenance/status`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setMaintenanceStatus({
              enabled: data.data.enabled === 1 || data.data.enabled === true,
              message: data.data.message || null,
              scheduled_at: data.data.scheduled_at || null,
              scheduled_message: data.data.scheduled_message || null,
            });
          }
        } else if (response.status === 404) {
          // Route n'existe pas, ignorer silencieusement
          return;
        }
      } catch (error: any) {
        // Ignorer les erreurs 404 et timeout silencieusement (la route peut ne pas exister)
        if (error.name !== 'AbortError' && error.message && !error.message.includes('404')) {
          // Log seulement les autres erreurs en mode debug
          console.debug('Maintenance status check failed:', error);
        }
      }
    };

    checkMaintenanceStatus();
    const interval = setInterval(checkMaintenanceStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLoginSuccess = (user: User) => {
    setShowLogin(false);
    if (maintenanceStatus?.enabled && user.roles_id === 1) {
      navigate('/panel');
      return;
    }
    navigate('/dashboard');
  };

  const getTopOffset = () => {
    let offset = 64; // Navbar height
    offset += 70; // DevBanner height
    if (maintenanceStatus?.scheduled_at && !maintenanceStatus?.enabled) offset += 80;
    if (showDeletionNotice) offset += 80;
    return offset;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      
      {/* Navbar */}
      <Navbar onLoginClick={() => setShowLogin(true)} onSignupClick={() => setShowSignup(true)} />
      
      {/* DevBanner - Positionné juste sous la Navbar */}
      <DevBanner />
      
      {/* Banners */}
      <div style={{ marginTop: getTopOffset() }}>
        {maintenanceStatus?.scheduled_at && !maintenanceStatus?.enabled && (
          <NotificationBanner
            type="scheduled"
            scheduledAt={maintenanceStatus.scheduled_at}
            scheduledMessage={maintenanceStatus.scheduled_message || undefined}
          />
        )}
        {showDeletionNotice && <NotificationBanner type="deletion" />}
      </div>

      {/* Sections : en maintenance, page dédiée + pas d’inscription publique */}
      {maintenanceStatus?.enabled ? (
        <MaintenancePublicPage
          message={maintenanceStatus.message}
          onOpenLogin={() => setShowLogin(true)}
        />
      ) : (
        <>
          <HeroSection
            onLogin={() => setShowLogin(true)}
            onSignup={() => setShowSignup(true)}
          />
          <ServicesSection />
          <AdminSection />
          <ContactSection />
          <PartnersSection />
        </>
      )}
      <Footer />

      {/* Modals */}
      {showLogin && (
        <LoginModal
          isOpen={showLogin}
          onClose={() => setShowLogin(false)}
          onSwitchToSignup={() => {
            setShowLogin(false);
            setShowSignup(true);
          }}
          onSuccess={handleLoginSuccess}
          maintenanceCmOnly={Boolean(maintenanceStatus?.enabled)}
        />
      )}
      {showSignup && !maintenanceStatus?.enabled && (
        <SignupModal
          isOpen={showSignup}
          onClose={() => setShowSignup(false)}
          onSwitchToLogin={() => {
            setShowSignup(false);
            setShowLogin(true);
          }}
          onSuccess={handleLoginSuccess}
        />
      )}
    </div>
  );
};

export default Home;
